import * as React from "react";
import styled, { css } from "styled-components";
import * as _ from "lodash";
import Downshift from "downshift";

import { media } from "src/library/utils/styled-media-queries/index";
import Grid from "src/library/components/Hedron";
import { Icon } from "src/library/components/Icon";
import { Text } from "src/library/components/Text";
import { InputCss } from "src/library/components/Forms/Input";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import { Label } from "src/library/components/Forms";
import { Icons } from "src/library/components/Icon/types";
import {
  useProductTagsApi,
  ITag
} from "src/modules/product-tags/api/product-tags.api";

import ProductTag from "./ProductTag";

interface ProductTagSelectorWrapperProps {
  productTags: ITag[];
  productId: number;
}

/**
 *
 * Component to handle order additions
 */
const ProductTagSelector: React.ForwardRefRenderFunction<{currentTags: ITag[]}, ProductTagSelectorWrapperProps> = ({ productTags }, ref) => {
  const [ listTagsResponse, listTagsRequest ] = useProductTagsApi("LIST_PRODUCT_TAGS");
  const [ searchResults, setSearchResults ] = React.useState<ITag[]>([]);
  const [ currentTags, setCurrentTags ] = React.useState(productTags);

  // get all tags available
  React.useEffect(() => {
    listTagsRequest({});
  }, []);

  /**
   * Search from all available tags
   */
  const performSearch = React.useCallback(
    (searchString: string) => {
      if (listTagsResponse.data) {
        const filteredResults = _.filter(listTagsResponse.data, foundTag => {
          // if tag already attached dont display it
          const exists = _.find(currentTags, tag => tag.id === foundTag.id);

          if (exists) {
            return false;
          }

          // if string match
          if (foundTag.label.toLocaleLowerCase().includes(searchString.toLocaleLowerCase())) {
            return true;
          }

          return false;
        });

        setSearchResults(filteredResults);
      }
    },
    [ listTagsResponse, currentTags ]
  );

  /**
   * Add tag to list
   */
  const addTag = React.useCallback(
    (productTag: ITag | null) => {
      if (productTag) {
        if (!_.find(currentTags, tag => tag.id === productTag.id)) {
          setCurrentTags([ productTag, ...currentTags ]);
        }
      }
    },
    [ currentTags ]
  );

  /**
   * The remove tag from list
   */
  const removeTag = React.useCallback(
    (index: number) => {
      if (currentTags[ index ]) {
        currentTags.splice(index, 1);
        setCurrentTags([ ...currentTags ]);
      }
    },
    [ currentTags ]
  );

  // return current tags to  parent
  React.useImperativeHandle(ref, () => ({ currentTags }), [ currentTags ]);

  return (
    <ProductTagSelectorWrapper>
      <Text
        as="h3"
        fontSize="h3"
      >
        Allergens, Warnings and Nutrition Tags
      </Text>

      <Downshift
        onSelect={addTag}
        itemToString={() => ""}
      >
        {({
          getInputProps, isOpen, getItemProps, highlightedIndex
        }) => {
          return (
            <div>
              <Label
                label="Find an allergen, warning or nutrition tag"
                for="tag-search"
              />

              <div
                style={{
                  width: "100%",
                  height: "100%",
                  position: "relative"
                }}
              >
                <div className="search-input-wrapper">
                  <Icon
                    className="search-icon"
                    icon={Icons.SEARCH}
                    $fillColor="lightGrey"
                  />

                  {/*
                  // @ts-ignore */}
                  <input
                    placeholder="Start typing to search..."
                    {...getInputProps({
                      id: "tag-search",
                      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                        e.persist();
                        performSearch(e.target.value);
                      }
                    })}
                  />
                </div>

                {isOpen && (
                  <SearchResults searchResultsCount={searchResults.length}>
                    {searchResults.map((item, index) => {
                      return (
                        <ProductTagSearchItemWrapper
                          {...getItemProps({ item })}
                          key={`template-${item.id}`}
                          highlighted={index === highlightedIndex}
                        >
                          <Text>
                            {item.label}
                          </Text>
                        </ProductTagSearchItemWrapper>
                      );
                    })}

                    {/* {props.error && (
                  <DropdownItem>
                    <span className="error">{`An unknown error occurred whilst performing your search, please try again.`}</span>
                  </DropdownItem>
                )} */}
                    {listTagsResponse.pending && (
                      <LoadingSpinner
                        position="inline"
                        type="flat"
                      />
                    )}

                    {!searchResults.length && !listTagsResponse.pending && (
                      <ProductTagSearchItemWrapper className="nothing-found">
                        <Text>
                          Nothing found
                        </Text>
                      </ProductTagSearchItemWrapper>
                    )}
                  </SearchResults>
                )}
              </div>
            </div>
          );
        }}
      </Downshift>

      <Grid.Bounds
        className="selected-tags"
        direction="vertical"
      >
        <ProductTagSelectionRowWrapper>
          <Grid.Box className="tag-row-col icon">
            <Text
              fontSize="h3"
              weight="bold"
            >
              Label
            </Text>
          </Grid.Box>

          <Grid.Box className="tag-row-col  label" />

          <Grid.Box className="tag-row-col info">
            <Text
              fontSize="h3"
              weight="bold"
            >
              Additional Information
            </Text>
          </Grid.Box>

          <Grid.Box className="tag-row-col  close" />
        </ProductTagSelectionRowWrapper>

        {_.map(currentTags, (productTag, index) => (
          <ProductTagSelectionRow
            key={`tag-${productTag.id}`}
            index={index}
            {...productTag}
            onRemoveTag={removeTag}
          />
        ))}
      </Grid.Bounds>
    </ProductTagSelectorWrapper>
  );
};

const SearchResults = styled.div<{ searchResultsCount: number }>`
  z-index: 10;
  width: 400px;
  position: absolute;
  left: 0;
  bottom: 37px;
  overflow-x: none;
  overflow-y: auto;
  border-top: 0;
  box-shadow: 0 1px 8px -4px #75839a;
  ${({ theme }) => css`
    background: ${theme.colors.white};
  `}

  ${Text} {
    padding: 10px;
  }
`;

const ProductTagSearchItemWrapper = styled.div<{ highlighted?: boolean }>`
  ${({ theme, highlighted }) => css`
    position: relative;
    height: 50px;
    padding: 10px 0;
    display: flex;
    align-items: center;
    cursor: pointer;
    background: ${highlighted ? theme.colors.primaryDark : "white"};
    pointer-events: "all";
    border-bottom: 1px solid ${highlighted ? theme.colors.primaryDark : theme.colors.lightGrey};
    max-width: calc(100vw - 50px);
    span {
      color: ${highlighted ? "white" : theme.colors.primaryDark};
    }

    &.nothing-found {
      cursor: default;
    }
  `}
`;

interface IProductTagSelectionRowProps extends ITag {
  index: number;
  onRemoveTag: (tagIndex: number) => void;
}

const ProductTagSelectionRow: React.ReactFCWithChildren<IProductTagSelectionRowProps> = ({
  onRemoveTag, index, ...productTag
}) => (
  <ProductTagSelectionRowWrapper key={`tag-row-${index}`}>
    <Grid.Box className="tag-row-col icon">
      <ProductTag {...productTag} />
    </Grid.Box>

    <Grid.Box className="tag-row-col  label">
      <Text weight="bold">
        {productTag.label}
      </Text>
    </Grid.Box>

    <Grid.Box className="tag-row-col info">
      <Text weight="bold">
        {productTag.info}
      </Text>
    </Grid.Box>

    <Grid.Box
      className="tag-row-col  close"
      onClick={() => onRemoveTag(index)}
    >
      <Icon
        icon={Icons.CLOSE}
        $fillColor="midGrey"
      />
    </Grid.Box>
  </ProductTagSelectionRowWrapper>
);

const ProductTagSelectionRowWrapper = styled(Grid.Bounds)`
  ${({ theme }) => css`
    .tag-row-col {
      padding: ${theme.spacing.padding.sm} 0;
      display: flex;
      justify-content: space-between;
      align-items: center;

      &.icon,
      &.close {
        width: 40px;
      }
      &.label {
        width: 100px;
      }

      &.info {
        width: 250px;
      }
    }
  `}
`;

const ProductTagSelectorWrapper = styled.div`
  ${({ theme }) => css`
    margin: ${theme.spacing.margins.xl} 0;
    .selected-tags {
      margin-top: ${theme.spacing.margins.md};
    }
    .search-input-wrapper {
      width: 400px;
      position: relative;

      .search-icon {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        left: 5px;
      }

      #tag-search {
        ${InputCss}
        padding-left: 40px;
        &:focus {
          ::placeholder {
            color: ${theme.colors[ "primaryDark" ]};
          }
        }

        ${media({ lt: theme.breakpoints.sm })} {
          font-size: 16px;
        }
      }
    }
  `}
`;

export default React.forwardRef(ProductTagSelector);
